import React, { useEffect, useMemo } from 'react';
import EditorBaseTheme from '../DaEditor/components/BaseTheme';
import isEqual from 'react-fast-compare';
import Notifications from '../Notifications';
import LiveNotifications from '../LiveNotifications';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import Modals from '../Modals';
import { NotificationRenderFunction } from '../Notifications/redux/types';
import { ModalsRenderFunction } from '../Modals/DaModal';
import Head from './Head';
import {
  PublicSessionState,
  SessionContext,
} from '@wix/da-react-context/pkg/publicSession';
import {
  ThemeOptions,
  ThemeSkin,
  ThemeContext,
  ThemeSkinContext,
} from '@wix/da-react-context/pkg/ThemeContext';
import { MatureContext } from '@wix/da-react-context/pkg/MatureContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { HeadingLevelContext } from '@wix/da-a11y/pkg/components/headings/context';
import {
  UniqueIdContext,
  makeUniqueIdSource,
} from '@wix/da-hooks/pkg/useUniqueId';
import { RuntimeEnvironment } from '../types';
import { FirstTimeFlag, FirstTimeFlagsType } from '../redux/firstTimeFlags';
import VerificationPopup from '../VerificationPopup';
import DuperbrowsePageWrapper from '../Duperbrowse/DuperbrowsePageWrapper';
import { BrowserFeatureContext } from '@wix/da-react-context/pkg/BrowserFeatureContext';
import { NCContext, NCContextProps } from '@wix/da-react-context/pkg/NCContext';
import {
  DamzContextProvider,
  DamzContextProps,
} from '@wix/da-react-context/pkg/DamzContext';
import { usePageVisibility } from '@wix/da-hooks/pkg/usePageVisibility';
import { ScreenSizeContextProvider } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { DuperbrowseViewType } from '../Duperbrowse/DuperbrowseViewContext';
import DRE from '../DRE';
import HoistCss from './HoistCss';
import TimeTravel from '../TimeTravel';
import { PageViewContextData } from '@wix/da-bi/pkg/events';
import GlobalModalPlacement from '../DealerAd/Placement/GlobalModal';
import DamzInit from '../Damz/DamzInit/DamzInit';

export interface Props {
  children: React.ReactNode;
  notificationsRenderFunc?: NotificationRenderFunction;
  notificationsWrapperClassName?: string;
  modalsRenderFunc?: ModalsRenderFunction;
  language: string;
  environment?: RuntimeEnvironment;
  bodyClassName?: string;
  disableHeadTags?: boolean;
  showMatureContent?: boolean;
  isMobile?: boolean;
  isLoggedIn?: boolean;
  hasJoinedWithEclipse?: boolean;
  isSisu?: boolean;
  isLegacyHeader?: boolean;
  theme?: ThemeOptions;
  themeSkin?: ThemeSkin;
  firstTimeFlags: FirstTimeFlagsType;
  publicSession: PublicSessionState;
  isDuperbrowseEnabled?: boolean;
  isDuperbrowseActive: boolean;
  disablePageWideNotifications?: boolean;
  disableSSRPopups?: boolean;
  supportsWebp?: boolean;
  supportsLazy?: boolean;
  biPageContextData: PageViewContextData;
  flags: string[];
  duperbrowseViewType?: DuperbrowseViewType;
  verificationEmail?: string;
  isIframe?: boolean;
  ncContextProps?: NCContextProps;
  damzContextProps?: DamzContextProps;
  /** equivalent native app url for the current page */
  appUrl?: string;
  /** show the install app thing if relevant */
  appMounted: () => void;
  appUnmounted: () => void;
  appVisibilityChange: (hidden: boolean) => void;
}

/**
 * This is designed to wrap your render() output in App.tsx
 * it provides the common components that we need for some
 * shared things.
 *
 * Things it includes:
 *
 * - <Notifications /> (for general system notifications when deleting, adding to groups, faving, etc)
 * - <LiveNotifications /> (for real time notifications pulled from hub)
 * - <Modals />
 *
 * The dream
 * This component wraps all the header/context etc but currently
 * there is some variation between projects, so for now its limited
 * in its scope.
 */
const DaApp: React.FC<React.PropsWithChildren<Props>> = ({
  appMounted,
  appUnmounted,
  appVisibilityChange,
  modalsRenderFunc,
  showMatureContent = true,
  isMobile = false,
  theme,
  themeSkin,
  language,
  environment,
  bodyClassName,
  isDuperbrowseEnabled = true,
  isDuperbrowseActive,
  isLegacyHeader,
  supportsWebp = false,
  supportsLazy = false,
  publicSession,
  biPageContextData,
  disableHeadTags,
  isLoggedIn,
  firstTimeFlags,
  children,
  notificationsRenderFunc,
  notificationsWrapperClassName,
  disablePageWideNotifications,
  disableSSRPopups,
  flags,
  duperbrowseViewType,
  isIframe,
  appUrl,
  ncContextProps = {},
  damzContextProps = {},
}) => {
  useEffect(() => {
    appMounted();
    return appUnmounted;
  }, [appMounted, appUnmounted]);
  usePageVisibility(appVisibilityChange);

  const browserSupport = useMemo(
    () => ({ supportsWebp, supportsLazy }),
    [supportsLazy, supportsWebp]
  );

  const uniqueIdSource = useMemo(() => makeUniqueIdSource('app-root'), []);

  const ncContextPropsMemo = useMemo(
    () => ncContextProps,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ncContextProps.DrawerComponent,
      ncContextProps.PageComponent,
      ncContextProps.isStandalonePage,
    ]
  );

  const renderPageContent = () => {
    return (
      <>
        {children}
        {!disablePageWideNotifications && (
          <Notifications wrapperClassName={notificationsWrapperClassName}>
            {notificationsRenderFunc}
          </Notifications>
        )}

        <LiveNotifications />
      </>
    );
  };

  const renderSSRPopups = () => {
    const unverified = isLoggedIn && !publicSession.isVerified;
    const hasNotSeenVerificationPopup =
      firstTimeFlags[FirstTimeFlag.WelcomePopupNotClosed];
    if (unverified && hasNotSeenVerificationPopup) {
      return <VerificationPopup dropFlagOnClose />;
    }
  };

  // the error boundary here is really a last ditch effort - if the error
  // got here the entire page is dead already so we're just saving face
  return (
    <ErrorBoundary debugComponent="DaApp">
      <SessionContext.Provider value={publicSession}>
        {!disableHeadTags && (
          <Head
            environment={environment}
            language={language}
            theme={theme}
            bodyClassName={bodyClassName}
            themeSkin={themeSkin}
          />
        )}
        <EditorBaseTheme />
        <HoistCss />

        <BiLoggerContextProvider value={biPageContextData}>
          <BrowserFeatureContext.Provider value={browserSupport}>
            <MatureContext.Provider value={showMatureContent}>
              <MobileContext.Provider value={isMobile}>
                <HeadingLevelContext.Provider value={1}>
                  <UniqueIdContext.Provider value={uniqueIdSource}>
                    <ThemeContext.Provider value={theme}>
                      <ThemeSkinContext.Provider value={themeSkin}>
                        <ScreenSizeContextProvider>
                          <NCContext.Provider value={ncContextPropsMemo}>
                            <DamzContextProvider context={damzContextProps}>
                              {isDuperbrowseEnabled ? (
                                <DuperbrowsePageWrapper
                                  viewType={duperbrowseViewType}
                                >
                                  {renderPageContent()}
                                </DuperbrowsePageWrapper>
                              ) : (
                                renderPageContent()
                              )}
                              <GlobalModalPlacement />
                              <Modals>{modalsRenderFunc}</Modals>
                              {!disableSSRPopups && renderSSRPopups()}
                              {flags.includes('dre') && !isLegacyHeader && (
                                <DRE environment={environment} />
                              )}
                              {flags.includes('time_travel') &&
                                !isLegacyHeader && <TimeTravel />}
                              <DamzInit />
                            </DamzContextProvider>
                          </NCContext.Provider>
                        </ScreenSizeContextProvider>
                      </ThemeSkinContext.Provider>
                    </ThemeContext.Provider>
                  </UniqueIdContext.Provider>
                </HeadingLevelContext.Provider>
              </MobileContext.Provider>
            </MatureContext.Provider>
          </BrowserFeatureContext.Provider>
        </BiLoggerContextProvider>
      </SessionContext.Provider>
    </ErrorBoundary>
  );
};
DaApp.displayName = 'DaApp';

export default React.memo(DaApp, isEqual);
